import { Fragment } from 'react';
import { Box, Checkbox, FormControl, ListItemText, ListSubheader, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCountries, getUserCountries, updateCountries } from "../../../new-store/top-bar/actions";
import { topBarSelector } from "../../../new-store/top-bar/selectors";
import { enqueueNotification } from "../../../store/notification/action";
import { useTranslation } from "react-i18next";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';


const ChangeUserCountry = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // @ts-ignore
  const profile = useSelector(({ authentication }) => authentication.profile);
  const topBarState = useSelector(topBarSelector);

  const [userCountries, setUserCountries] = useState<number[]>([]);
  const [hasChanged, setHasChanged] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getUserCountries());
  }, [dispatch])

  useEffect(() => {
    if (topBarState.userCountries.fullfilled && topBarState.userCountries.data) {
      setUserCountries(topBarState.userCountries.data.map(c => c.id));
    }
  }, [topBarState.userCountries]);

  const handleClick = (id: number) => {
    if (userCountries.includes(id)) {
      const updatedValue = userCountries.filter(el => el !== id);

      if (updatedValue.length === 0) {
        dispatch(enqueueNotification(t('top_bar.no_countries_warning')));

        return;
      }

      setUserCountries(updatedValue);
    } else {
      const updatedValue = [...userCountries, id];
      setUserCountries(updatedValue);
    }

    setHasChanged(true);
  }

  const handleRegionClick = (e: any, countries) => {
    e.stopPropagation();

    // Filter out all countries first to update in case of deselect or to prevent duplicates
    const filteredValues = userCountries.filter(el => !countries.map(c => c.id).includes(el));

    if (countries.every(c => userCountries.includes(c.id))) {
      if (filteredValues.length === 0) {
        dispatch(enqueueNotification(t('top_bar.no_countries_warning')));

        return;
      }

      setUserCountries(filteredValues);
    } else {
      const updatedValues = [...filteredValues, ...countries.map(c => c.id)];
      setUserCountries(updatedValues);
    }

    setHasChanged(true);
  }

  const handleClose = async () => {
    if (hasChanged) {
      await dispatch(updateCountries({ countries: userCountries }));

      // @ts-ignore
      window.location = window.location.origin;
    }
  }

  const toggleRegion = (region: string) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev[region],
    }));
  };

  const groupedCountries: Record<string, any[]> = topBarState.countries.data.reduce((acc, cur) => {
    if (!acc[cur.region]) {
      acc[cur.region] = [];
    }

    acc[cur.region].push(cur);
    return acc;
  }, {} as Record<string, any[]>);

  if (!profile.canSwitchCountry || !topBarState.countries.fullfilled) return null;

  return (
    <FormControl>
      <Select
        sx={{ maxWidth: 150, height: 40, mr: 4 }}
        multiple
        value={userCountries}
        variant='standard'
        disableUnderline
        renderValue={(selected: number[]) => {
          const names = topBarState.countries.data.filter(c => selected.includes(c.id)).map(c => c.name).join(', ');

          return (
            <Tooltip title={names}>
              <Box sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}>
                {names}
              </Box>
            </Tooltip>
          )
        }}
        onClose={handleClose}
        MenuProps={{
          sx: {
            zIndex: 1320
          },
        }}
      >
        {
          Object.entries(groupedCountries)
            .sort(([keyA], [keyB]) =>
              keyA.localeCompare(keyB)
            )
            .map(([region, countries]) => (
              <Fragment key={region}>
                <ListSubheader
                  onClick={() => toggleRegion(region)}
                  sx={{ cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box>
                    <Checkbox
                      onClick={(e) => handleRegionClick(e, countries)}
                      checked={countries.every(c => userCountries.includes(c.id))}
                      indeterminate={countries.some(c => userCountries.includes(c.id)) && !countries.every(c => userCountries.includes(c.id))}
                    />  {region}
                  </Box>
                  <Box sx={{ pl: 4 }}>
                    <ChevronRightIcon sx={{ fontSize: 'medium', transform: `rotate(${expandedRegions[region] ? '90deg' : '0'})` }} />
                  </Box>
                </ListSubheader>
                {
                  expandedRegions[region] && countries
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(c => (
                    <MenuItem
                      id={c.id}
                      key={c.id}
                      value={c.id}
                      onClick={() => handleClick(c.id)}
                      sx={{ pl: 6 }}
                    >
                      <Checkbox checked={userCountries.includes(c.id)} />
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))
                }
              </Fragment>
            ))
        }
      </Select>
    </FormControl>
  )
}

export default ChangeUserCountry;
